import Supercluster from 'supercluster';
import _ from 'underscore';
import Utils from 'helpers/Utils';

const defaultIcon = {
    scale: 15,
    fillOpacity: 1,
    fillColor: 'orange',
    strokeWeight: 0
  },
  defaultLabel = {
    color: 'black',
    fontFamily: 'Arial',
    fontSize: '10px',
    fontWeight: 'bold'
  },
  POINT_COUNT = 'point_count';

/**  
 * @lends core.helpers.ClusterHelper.prototype
 * @class ClusterHelper 
 * @example <caption>Cluster/caption>
    "options": {
      "radius": 150,
      "maxZoom": 13,
      "minZoom": 0,
      "extend": 512,
      "nodeSize": 64,
      "log": false
    },
    "iconStyles": {
      "url": "cluster_orange.png",
      "anchor": { "x": 17, "y": 17 }
    }
   */
export default class ClusterHelper {

  /**
   * @param {core.Config.Cluster} options The options of the clusters
   */
  constructor(options) {
    this.options = options;
    this.widget = options.widget;
    this.createClusterService();
  }

  /**
   * @typedef {object} Cluster 
   * @property {core.Config.SuperClusterOptions} options The type of the layer. Must be APILayer
   * @property {google.maps.Icon} iconStyles To activate the layer by default
   * @memberof core.Config 
   */

  /**
   * @typedef {object} SuperClusterOptions 
   * @property {number} [radius=40] Cluster radius, in pixels.
   * @property {number} [minZoom=0] Minimum zoom level at which clusters are generated.
   * @property {number} [maxZoom=16] Maximum zoom level at which clusters are generated.
   * @property {number} [extent=512] (Tiles) Tile extent. Radius is calculated relative to this value.
   * @property {number} [nodeSize=64] Size of the KD-tree leaf node. Affects performance.
   * @property {boolean} [log=false] Whether timing info should be logged.
   * @memberof core.Config 
   */

  /**
   * Create the cluster service
   */
  createClusterService() {
    this.clusterConf = _.isObject(this.options.cluster) ? this.options.cluster : {};
    this.cluster = Supercluster(this.clusterConf.options);
  }

  /**
   * This method cluster features
   * @param {Feature[]} features 
   * @return Return the features clustered
   */
  clusterFeatures(features) {
    this.cluster.load(features);
    let bboxJSON;
    if (this.options.filterByBBox) {
      bboxJSON = this.widget.map.getBounds().toJSON();
    } else {
      bboxJSON = { west: -180, south: -90, east: 180, north: 90 };
    }
    let superclusterBBox = [bboxJSON.west, bboxJSON.south, bboxJSON.east, bboxJSON.north];
    return this.cluster.getClusters(superclusterBBox, this.widget.map.getZoom());
  }

  /**
   * Fit bounds of the features clustered
   * @param {string} clusterId The cluster id
   * @param {number} zoom Zoom the cluster was from
   * @param {object} map The map
   */
  setClusterExpansion(clusterId) {
    let features = this.cluster.getLeaves(clusterId, this.widget.map.getZoom(), Infinity);
    let boundingBox = new google.maps.LatLngBounds();
    features.forEach((feature) => {
      let featurePosition = new google.maps.LatLng(feature.geometry.coordinates[1],
        feature.geometry.coordinates[0]);
      boundingBox.extend(featurePosition);
    });
    this.widget.map.fitBounds(boundingBox);
  }

  /**
   * Get the icon of the cluster 
   * @param {Widget} widget The widget
   */
  getIconCluster(widget) {
    if (this.clusterConf.iconStyles) {
      let iconStyles = _.extend({}, this.clusterConf.iconStyles);
      iconStyles.url = Utils.getImgUrl(this.clusterConf.iconStyles.url, widget);
      return iconStyles;
    } else {
      return _.extend(defaultIcon, {
        path: google.maps.SymbolPath.CIRCLE
      });
    }
  }

  /**
   * Get the label of the cluster
   * @param {number} numFeatures 
   */
  getLabelCluster(numFeatures) {
    let labelStyles = this.clusterConf.labelStyles ? this.clusterConf.labelStyles : defaultLabel;
    return _.extend(labelStyles, { text: numFeatures.toString() });
  }

  /**
   * Create a unique id of a cluster
   * @param {Feature} feature 
   */
  getClusterId(feature) {
    return String(feature.geometry.coordinates[0]) +
      String(feature.geometry.coordinates[1]) +
      String(feature.properties[POINT_COUNT]);
  }

}



// WEBPACK FOOTER //
// ./lmm-widget/src/helpers/ClusterHelper.js